import { useState } from "react";
import { useLocation } from "wouter";
import Logo from "@/components/mnf/Logo";
import { GlassCard, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { mutations, useStoreQuery, queries, type InjurySelection, type FitnessGoal, type WeekDay, type InjuryFlag } from "@/lib/store";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

type Step =
  | "welcome"
  | "lifestage"
  | "goals"
  | "injuries"
  | "movement-goals"
  | "schedule"
  | "translation"
  | "tier"
  | "garden"
  | "ready";

/** Four NASM injury buckets we route exercise contraindications by. */
const INJURY_OPTIONS: { flag: InjuryFlag; title: string; sub: string; emoji: string }[] = [
  { flag: "joints",       title: "Joints",       sub: "Knees, hips, shoulders, wrists, ankles", emoji: "🦵" },
  { flag: "spine",        title: "Spine",        sub: "Low back, neck, disc issues, scoliosis",  emoji: "🌿" },
  { flag: "pelvic-floor", title: "Pelvic floor", sub: "Diastasis, prolapse, leaking, heaviness", emoji: "🤍" },
  { flag: "other",        title: "Something else", sub: "Tendinitis, hernia, recovery from surgery", emoji: "🩹" },
];

const MOVEMENT_GOAL_OPTIONS: { value: FitnessGoal; label: string; emoji: string; sub: string }[] = [
  { value: "build-strength",       label: "Build strength",        emoji: "💪", sub: "Heavier compounds, NASM 4-week blocks" },
  { value: "lose-weight",          label: "Tend my weight gently", emoji: "🌾", sub: "More volume, kettlebells, intervals" },
  { value: "flexibility",          label: "Mobility and flexibility", emoji: "🧘", sub: "Hip openers, T-spine, end-range work" },
  { value: "postpartum-recovery",  label: "Postpartum recovery",   emoji: "🌷", sub: "GGS V2 protocol, pelvic floor first" },
];

const WEEKDAY_OPTIONS: { value: WeekDay; label: string; short: string }[] = [
  { value: 0, label: "Sun", short: "S" },
  { value: 1, label: "Mon", short: "M" },
  { value: 2, label: "Tue", short: "T" },
  { value: 3, label: "Wed", short: "W" },
  { value: 4, label: "Thu", short: "T" },
  { value: 5, label: "Fri", short: "F" },
  { value: 6, label: "Sat", short: "S" },
];

const LIFESTAGES = [
  { key: "cycling",       title: "Cycling regularly", sub: "Tracking phases for training + nourishment", emoji: "🌙" },
  { key: "trying",        title: "Trying to conceive", sub: "Tender support for this season",            emoji: "🌱" },
  { key: "pregnant",      title: "Carrying a baby",   sub: "Movement and meals for two",                 emoji: "🤍" },
  { key: "postpartum",    title: "Postpartum",        sub: "Healing, gentle return, fed and held",       emoji: "🌷" },
  { key: "perimenopause", title: "Perimenopause",     sub: "Strength training, hormone-aware fueling",   emoji: "🍂" },
  { key: "menopause",     title: "Menopause +",       sub: "Bone, heart, and joy through this passage",  emoji: "🌿" },
];

const GOALS = [
  { key: "strength",   label: "Build strength",            emoji: "💪" },
  { key: "energy",     label: "More daily energy",         emoji: "⚡" },
  { key: "weight",     label: "Tend my weight gently",     emoji: "🌾" },
  { key: "habits",     label: "Daily rhythm with God",     emoji: "🙏" },
  { key: "family",     label: "Feed my family well",       emoji: "🍽️" },
  { key: "community",  label: "Walk with sisters",         emoji: "🤝" },
];

const TRANSLATIONS = [
  { key: "ESV", name: "English Standard Version", sub: "Word-for-word, contemporary" },
  { key: "NIV", name: "New International",        sub: "Most widely read in churches" },
  { key: "KJV", name: "King James",                sub: "Classic poetic English" },
  { key: "NLT", name: "New Living",                sub: "Easy, conversational" },
  { key: "NKJV", name: "New King James",           sub: "Traditional with modern grammar" },
  { key: "CSB", name: "Christian Standard",        sub: "Clear and faithful" },
];

const TIERS = [
  {
    key: "Seedling",
    price: "$47",
    color: "#A8D4B8",
    text: "#2C6248",
    bg: "rgba(168,212,184,0.18)",
    perks: ["Garden food log", "Move workouts", "Daily verse", "Sister wall (read)"],
  },
  {
    key: "Rooted",
    price: "$97",
    featured: true,
    color: "var(--mnf-f)",
    text: "#F5F0E8",
    bg: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
    perks: ["Everything in Seedling", "Coach Kiley voice notes", "Family meal plans", "Cycle-aware training", "Plant a seed in prayer wall"],
  },
  {
    key: "Flourishing",
    price: "$197",
    color: "var(--mnf-t)",
    text: "#fff",
    bg: "linear-gradient(165deg, #C0784A 0%, #A86638 100%)",
    perks: ["Everything in Rooted", "1:1 monthly with Coach", "Custom plan builder", "Mentor a younger sister"],
  },
];

interface Answers {
  name: string;
  lifestage?: string;
  goals: string[];
  injuries: InjurySelection[];
  movementGoals: FitnessGoal[];
  scheduleDays: WeekDay[];
  scheduleMinutes: number;
  translation: string;
  tier: string;
  zipCode: string;
  city: string;
}

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<Step>("welcome");
  // The signed-in sister's own id. finish() previously wrote every answer to
  // the hardcoded seed account instead of this.
  const me = useStoreQuery(() => queries.currentUser());
  const userId = me?.id;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>({
    name: "",
    goals: [],
    injuries: [],
    movementGoals: [],
    scheduleDays: [1, 3, 5],
    scheduleMinutes: 45,
    translation: "ESV",
    tier: "Rooted",
    zipCode: "",
    city: "",
  });

  const stepIndex: Record<Step, number> = {
    welcome: 0,
    lifestage: 1,
    goals: 2,
    injuries: 3,
    "movement-goals": 4,
    schedule: 5,
    translation: 6,
    tier: 7,
    garden: 8,
    ready: 9,
  };
  const progress = (stepIndex[step] / 9) * 100;

  const next = (to: Step) => setStep(to);
  const setUserLocationOnServer = useConvexMutation(api.users.setUserLocation);

  const finish = async () => {
    // Every answer below used to be written to the seeded demo account's
    // literal id, while a real sister gets a generated id. So she
    // completed all ten steps, none of it saved against her, and Today still
    // said "Set up plan". First three minutes, dead end. This is the single
    // reason onboarding never completed for anyone but the seed user.
    if (!userId) {
      // No signed-in user: do not silently write onboarding to the wrong
      // account. Surface it rather than pretending it saved.
      setSaveError("We couldn't save your answers — please sign in and try again.");
      return;
    }
    try {
      if (import.meta.env.DEV) {
        (window as any).__mnf_user__ = answers;
      }
      if (answers.zipCode) {
        mutations.setUserLocation(userId, answers.zipCode, answers.city || "");
        // ...and to Convex, so it survives this browser. convex/users.ts
        // setUserLocation was fully written and had ZERO call sites: her zip
        // reached only the in-memory store, which meant the users.by_zip index
        // and every "near you" read had nothing real to match on. The server
        // resolves the owner from the session (authHelpers.resolveUserId
        // ignores the client-supplied id), so this cannot write to anyone else.
        // Best-effort: a location failure must not strand her at step 10 with
        // the rest of her answers already saved.
        try {
          await setUserLocationOnServer({
            userId,
            zipCode: answers.zipCode,
            city: answers.city || "",
          });
        } catch (err) {
          console.error("[onboarding] could not save location to server", err);
        }
      }
      // Save NASM/GGS personalization profile
      mutations.setInjuries(userId, answers.injuries);
      mutations.setFitnessGoals(userId, answers.movementGoals);
      mutations.setSchedule(userId, {
        days: answers.scheduleDays,
        minutesPerDay: answers.scheduleMinutes,
      });
      mutations.completeMovementOnboarding(userId);
    } catch (err) {
      // This was `catch {}`. A swallowed failure here looks identical to
      // success and strands her on a half-configured account.
      console.error("[onboarding] failed to save profile", err);
      setSaveError("Something went wrong saving your answers. Please try again.");
      return;
    }
    setLocation("/");
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(168,212,184,0.35), transparent 60%)," +
          "radial-gradient(900px 500px at 110% 20%, rgba(192,120,74,0.18), transparent 60%)," +
          "linear-gradient(180deg, #EDE3D2 0%, #F5F0E8 60%, #EFE7D6 100%)",
      }}
    >
      {/* Header bar */}
      <div className="w-full max-w-md px-6 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo size={20} />
          <span className="mnf-serif italic" style={{ fontSize: 15, color: "var(--mnf-f)" }}>Move Nourish Flourish</span>
        </div>
        {step !== "welcome" && step !== "ready" && (
          <button
            onClick={() => {
              const order: Step[] = [
                "welcome",
                "lifestage",
                "goals",
                "injuries",
                "movement-goals",
                "schedule",
                "translation",
                "tier",
                "garden",
                "ready",
              ];
              const i = order.indexOf(step);
              if (i > 0) setStep(order[i - 1]);
            }}
            className="mnf-body"
            style={{ fontSize: 12, color: "var(--mnf-tg)", letterSpacing: "0.04em" }}
            data-testid="button-back"
          >
            ← Back
          </button>
        )}
      </div>

      {/* Progress */}
      <div className="w-full max-w-md px-6 pb-6">
        <div style={{ height: 4, borderRadius: 999, background: "rgba(30,74,53,0.08)", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "linear-gradient(90deg, var(--mnf-f) 0%, var(--mnf-t) 100%)",
              borderRadius: 999,
              transition: "width 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          />
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 w-full max-w-md px-6 pb-10 flex flex-col">
        {step === "welcome" && (
          <WelcomeStep
            name={answers.name}
            onName={(name) => setAnswers({ ...answers, name })}
            onNext={() => next("lifestage")}
          />
        )}
        {step === "lifestage" && (
          <LifestageStep
            value={answers.lifestage}
            onPick={(lifestage) => { setAnswers({ ...answers, lifestage }); next("goals"); }}
          />
        )}
        {step === "goals" && (
          <GoalsStep
            value={answers.goals}
            onChange={(goals) => setAnswers({ ...answers, goals })}
            onNext={() => next("injuries")}
          />
        )}
        {step === "injuries" && (
          <InjuriesStep
            value={answers.injuries}
            onChange={(injuries) => setAnswers({ ...answers, injuries })}
            onNext={() => next("movement-goals")}
          />
        )}
        {step === "movement-goals" && (
          <MovementGoalsStep
            value={answers.movementGoals}
            onChange={(movementGoals) => setAnswers({ ...answers, movementGoals })}
            onNext={() => next("schedule")}
          />
        )}
        {step === "schedule" && (
          <ScheduleStep
            days={answers.scheduleDays}
            minutes={answers.scheduleMinutes}
            onChange={(scheduleDays, scheduleMinutes) =>
              setAnswers({ ...answers, scheduleDays, scheduleMinutes })
            }
            onNext={() => next("translation")}
          />
        )}
        {step === "translation" && (
          <TranslationStep
            value={answers.translation}
            onPick={(translation) => { setAnswers({ ...answers, translation }); next("tier"); }}
          />
        )}
        {step === "tier" && (
          <TierStep
            value={answers.tier}
            onPick={(tier) => { setAnswers({ ...answers, tier }); next("garden"); }}
          />
        )}
        {step === "garden" && (
          <GardenStep
            zipCode={answers.zipCode}
            city={answers.city}
            onChange={(zipCode, city) => setAnswers({ ...answers, zipCode, city })}
            onNext={() => next("ready")}
            onSkip={() => next("ready")}
          />
        )}
        {step === "ready" && (
          <>
            <ReadyStep answers={answers} onFinish={finish} />
            {saveError && (
              <p
                role="alert"
                className="mt-3 text-center text-sm"
                style={{ color: "#9A3B32" }}
              >
                {saveError}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- step components --------------------------- */

function StepHeader({ eyebrow, title, sub }: { eyebrow?: string; title: string; sub?: string }) {
  return (
    <div className="mb-6">
      {eyebrow && <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-t)" }}>{eyebrow}</p>}
      <h1 className="mnf-serif" style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
        {title}
      </h1>
      {sub && (
        <p className="mnf-body mt-3" style={{ fontSize: 14, color: "var(--mnf-tg)", lineHeight: 1.55 }}>{sub}</p>
      )}
    </div>
  );
}

function WelcomeStep({ name, onName, onNext }: { name: string; onName: (s: string) => void; onNext: () => void }) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 flex items-center justify-center py-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center mb-6" style={{
            width: 96, height: 96, borderRadius: "60% 30% 60% 30%",
            background: "linear-gradient(135deg, rgba(168,212,184,0.4) 0%, rgba(192,120,74,0.18) 100%)",
            border: "1px solid rgba(30,74,53,0.10)",
          }}>
            <Logo size={48} />
          </div>
          <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-t)" }}>Welcome, sister</p>
          <h1 className="mnf-serif" style={{ fontSize: 36, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.05, letterSpacing: "-0.01em" }}>
            A garden for body,<br /><span className="italic">soul, and sisterhood.</span>
          </h1>
          <p className="mnf-body italic mt-4" style={{ fontSize: 14, color: "var(--mnf-tg)", lineHeight: 1.55, maxWidth: 320, margin: "16px auto 0" }}>
            "She is clothed with strength and dignity, and she laughs without fear of the future." Proverbs 31:25
          </p>
        </div>
      </div>

      <GlassCard padding={20} className="mb-4">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>What shall we call you?</label>
        <input
          data-testid="input-name"
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="Hannah"
          className="w-full mnf-serif italic outline-none bg-transparent"
          style={{
            fontSize: 24, color: "var(--mnf-fd)",
            borderBottom: "1px solid rgba(30,74,53,0.18)",
            paddingBottom: 8,
          }}
        />
      </GlassCard>

      <Btn variant="primary" block size="lg" onClick={onNext} disabled={!name.trim()} data-testid="button-begin">
        Begin <Icon name="arrow_forward" size={16} />
      </Btn>
    </div>
  );
}

function LifestageStep({ value, onPick }: { value?: string; onPick: (k: string) => void }) {
  return (
    <div>
      <StepHeader
        eyebrow="Step 1 of 8"
        title="Where are you in your season?"
        sub="So Coach Kiley can tend you well. You can change this any time."
      />
      <div className="space-y-2.5">
        {LIFESTAGES.map((l) => {
          const active = value === l.key;
          return (
            <button
              key={l.key}
              onClick={() => onPick(l.key)}
              data-testid={`button-lifestage-${l.key}`}
              className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
              style={{
                padding: "14px 16px",
                borderRadius: 18,
                background: active ? "rgba(192,120,74,0.10)" : "rgba(255,255,255,0.65)",
                border: `1px solid ${active ? "var(--mnf-t)" : "rgba(30,74,53,0.06)"}`,
              }}
            >
              <span style={{ fontSize: 26 }} aria-hidden>{l.emoji}</span>
              <div className="flex-1">
                <p className="mnf-body" style={{ fontSize: 14, fontWeight: 700, color: "var(--mnf-fd)" }}>{l.title}</p>
                <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.4 }}>{l.sub}</p>
              </div>
              <Icon name={active ? "check_circle" : "chevron_right"} size={20} weight={500} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GoalsStep({ value, onChange, onNext }: { value: string[]; onChange: (v: string[]) => void; onNext: () => void }) {
  const toggle = (k: string) => {
    if (value.includes(k)) onChange(value.filter((x) => x !== k));
    else onChange([...value, k]);
  };
  return (
    <div className="flex-1 flex flex-col">
      <StepHeader
        eyebrow="Step 2 of 8"
        title="What is the Lord stirring in you?"
        sub="Pick as many as feel true. We'll shape your daily rhythm around them."
      />
      <div className="grid grid-cols-2 gap-3 flex-1">
        {GOALS.map((g) => {
          const active = value.includes(g.key);
          return (
            <button
              key={g.key}
              onClick={() => toggle(g.key)}
              data-testid={`button-goal-${g.key}`}
              className="flex flex-col items-start justify-between text-left transition-all hover-elevate active-elevate-2"
              style={{
                padding: 16,
                borderRadius: "24px 8px 24px 8px",
                background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)"}`,
                color: active ? "#F5F0E8" : "var(--mnf-fd)",
                minHeight: 110,
              }}
            >
              <span style={{ fontSize: 26 }} aria-hidden>{g.emoji}</span>
              <span className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.2 }}>{g.label}</span>
            </button>
          );
        })}
      </div>
      <Btn variant="primary" block size="lg" onClick={onNext} disabled={value.length === 0} className="mt-6" data-testid="button-continue">
        Continue <Icon name="arrow_forward" size={16} />
      </Btn>
    </div>
  );
}

function TranslationStep({ value, onPick }: { value: string; onPick: (k: string) => void }) {
  return (
    <div>
      <StepHeader
        eyebrow="Step 6 of 8"
        title="Which Word feels like home?"
        sub="Your daily verses and devotions will be served in this translation."
      />
      <div className="space-y-2.5">
        {TRANSLATIONS.map((t) => {
          const active = value === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onPick(t.key)}
              data-testid={`button-translation-${t.key}`}
              className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
              style={{
                padding: "14px 16px",
                borderRadius: 18,
                background: active ? "rgba(127,119,221,0.10)" : "rgba(255,255,255,0.65)",
                border: `1px solid ${active ? "var(--mnf-purple)" : "rgba(30,74,53,0.06)"}`,
              }}
            >
              <span
                className="mnf-serif italic"
                style={{ fontSize: 18, fontWeight: 500, color: active ? "var(--mnf-purple)" : "var(--mnf-fd)", minWidth: 56 }}
              >
                {t.key}
              </span>
              <div className="flex-1">
                <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>{t.name}</p>
                <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>{t.sub}</p>
              </div>
              <Icon name={active ? "check_circle" : "chevron_right"} size={20} weight={500} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TierStep({ value, onPick }: { value: string; onPick: (k: string) => void }) {
  return (
    <div>
      <StepHeader
        eyebrow="Step 7 of 8"
        title="Choose your sanctuary"
        sub="Every tier includes a 7-day gentle trial. No card today."
      />
      <div className="space-y-3">
        {TIERS.map((t) => {
          const active = value === t.key;
          const dark = t.featured || t.key === "Flourishing";
          return (
            <button
              key={t.key}
              onClick={() => onPick(t.key)}
              data-testid={`button-tier-${t.key.toLowerCase()}`}
              className="w-full text-left transition-all hover-elevate active-elevate-2 relative overflow-hidden"
              style={{
                padding: 18,
                borderRadius: 22,
                background: dark ? t.bg : "rgba(255,255,255,0.75)",
                border: `1px solid ${active ? (dark ? "rgba(245,240,232,0.4)" : "var(--mnf-f)") : "rgba(30,74,53,0.06)"}`,
                color: dark ? t.text : "var(--mnf-fd)",
                boxShadow: active ? "0 8px 24px rgba(30,74,53,0.18)" : undefined,
              }}
            >
              {t.featured && (
                <span
                  className="absolute top-3 right-3 mnf-body"
                  style={{
                    padding: "3px 9px", borderRadius: 999,
                    background: "var(--mnf-gold2)", color: "var(--mnf-gold)",
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  }}
                >
                  Most chosen
                </span>
              )}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="mnf-serif" style={{ fontSize: 24, fontWeight: 500 }}>{t.key}</span>
              </div>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="mnf-serif" style={{ fontSize: 28, fontWeight: 500 }}>{t.price}</span>
                <span className="mnf-body" style={{ fontSize: 12, opacity: dark ? 0.7 : 0.6 }}>/ month</span>
              </div>
              <ul className="space-y-1.5">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2 mnf-body" style={{ fontSize: 12.5 }}>
                    <Icon name="check" size={14} weight={500} /> {p}
                  </li>
                ))}
              </ul>
              {active && (
                <div className="mt-3 flex items-center gap-1.5 mnf-body" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  <Icon name="check_circle" size={14} weight={600} /> Selected
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GardenStep({
  zipCode, city, onChange, onNext, onSkip,
}: {
  zipCode: string;
  city: string;
  onChange: (zip: string, city: string) => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const validZip = /^\d{5}$/.test(zipCode.trim());
  return (
    <div className="flex-1 flex flex-col">
      <StepHeader
        eyebrow="Step 8 of 8"
        title="Where is your garden growing?"
        sub="So we can lift up farmers markets, walking groups, faith events, and sister meetups near you. You can change this any time."
      />

      <GlassCard padding={20} className="mb-3">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>Zip code</label>
        <input
          data-testid="input-zip"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={zipCode}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5), city)}
          placeholder="37215"
          className="w-full mnf-serif italic outline-none bg-transparent tracking-widest"
          style={{
            fontSize: 28, color: "var(--mnf-fd)",
            borderBottom: "1px solid rgba(30,74,53,0.18)",
            paddingBottom: 8, letterSpacing: "0.18em",
          }}
        />
      </GlassCard>

      <GlassCard padding={20} className="mb-4">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>City &amp; state <span style={{ textTransform: "none", letterSpacing: 0, fontSize: 10, opacity: 0.7 }}>(optional)</span></label>
        <input
          data-testid="input-city"
          value={city}
          onChange={(e) => onChange(zipCode, e.target.value)}
          placeholder="Nashville, TN"
          className="w-full mnf-serif italic outline-none bg-transparent"
          style={{
            fontSize: 22, color: "var(--mnf-fd)",
            borderBottom: "1px solid rgba(30,74,53,0.18)",
            paddingBottom: 8,
          }}
        />
      </GlassCard>

      <div
        className="flex items-start gap-3 p-3 mb-4"
        style={{ background: "var(--mnf-fiberBg)", border: "1px solid var(--mnf-fiberBorder)", borderRadius: 16 }}
      >
        <span style={{ fontSize: 22 }} aria-hidden>🌿</span>
        <p className="mnf-body italic" style={{ fontSize: 12.5, color: "#2C6248", lineHeight: 1.5 }}>
          We never share your address. Just used to surface community happenings within a few miles of your front door.
        </p>
      </div>

      <div className="mt-auto space-y-2">
        <Btn variant="primary" block size="lg" onClick={onNext} disabled={!validZip} data-testid="button-zip-continue">
          Plant my garden <Icon name="arrow_forward" size={16} />
        </Btn>
        <button
          onClick={onSkip}
          data-testid="button-zip-skip"
          className="w-full mnf-body"
          style={{
            fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            color: "var(--mnf-tg)", padding: "10px 0", background: "transparent", border: "none", cursor: "pointer",
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function ReadyStep({ answers, onFinish }: { answers: Answers; onFinish: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
      <div className="inline-flex items-center justify-center mb-5" style={{
        width: 80, height: 80, borderRadius: 999,
        background: "var(--mnf-fiberBg)",
        border: "1px solid var(--mnf-fiberBorder)",
      }}>
        <Icon name="eco" size={40} weight={500} />
      </div>
      <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-t)" }}>The garden is ready</p>
      <h1 className="mnf-serif" style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
        Welcome,<br /><span className="italic">{answers.name || "sister"}.</span>
      </h1>
      <p className="mnf-body mt-4" style={{ fontSize: 14, color: "var(--mnf-tg)", lineHeight: 1.55, maxWidth: 320 }}>
        Coach Kiley is preparing your first week. Rooted tier on a 7-day gentle trial, in your {answers.translation} translation.
      </p>

      <GlassCard padding={16} className="mt-6 w-full text-left">
        <p className="mnf-eyebrow mb-3" style={{ color: "var(--mnf-tg)" }}>Your first week</p>
        <div className="space-y-2.5">
          {[
            { icon: "yard", label: "Daily food log starts tomorrow morning" },
            { icon: "fitness_center", label: "3 Strength & Grace sessions queued" },
            { icon: "menu_book", label: "First Word reflection at 6am" },
            { icon: "diversity_3", label: "Coach voice note within 24 hours" },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)" }}>
                <Icon name={it.icon} size={16} weight={500} />
              </span>
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)" }}>{it.label}</p>
            </div>
          ))}
        </div>
      </GlassCard>

      <div className="w-full mt-6">
        <Btn variant="primary" block size="lg" onClick={onFinish} data-testid="button-enter">
          Enter the garden 🌱
        </Btn>
        <p className="mnf-body mt-3" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
          You can change tier, translation, or season any time in Sanctuary.
        </p>
      </div>
    </div>
  );
}

/* ----------------------------- new movement steps ------------------------ */

function InjuriesStep({
  value, onChange, onNext,
}: {
  value: InjurySelection[];
  onChange: (v: InjurySelection[]) => void;
  onNext: () => void;
}) {
  const toggle = (flag: InjuryFlag) => {
    const exists = value.find((v) => v.flag === flag);
    if (exists) {
      onChange(value.filter((v) => v.flag !== flag));
    } else {
      onChange([...value, { flag, detail: "", paused: false }]);
    }
  };

  const setDetail = (flag: InjuryFlag, detail: string) => {
    onChange(value.map((v) => (v.flag === flag ? { ...v, detail } : v)));
  };

  const isSet = (flag: InjuryFlag) => value.some((v) => v.flag === flag);

  return (
    <div className="flex-1 flex flex-col">
      <StepHeader
        eyebrow="Step 3 of 8"
        title="Anything tender we should work around?"
        sub="Coach Kiley will swap or modify any movement that touches an active flag. You can pause or remove these any time in Settings."
      />
      <div className="space-y-2.5">
        {INJURY_OPTIONS.map((opt) => {
          const active = isSet(opt.flag);
          const selected = value.find((v) => v.flag === opt.flag);
          return (
            <div key={opt.flag}>
              <button
                onClick={() => toggle(opt.flag)}
                data-testid={`button-injury-${opt.flag}`}
                className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "14px 16px",
                  borderRadius: 18,
                  background: active ? "rgba(192,120,74,0.10)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-t)" : "rgba(30,74,53,0.06)"}`,
                }}
              >
                <span style={{ fontSize: 26 }} aria-hidden>{opt.emoji}</span>
                <div className="flex-1">
                  <p className="mnf-body" style={{ fontSize: 14, fontWeight: 700, color: "var(--mnf-fd)" }}>{opt.title}</p>
                  <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.4 }}>{opt.sub}</p>
                </div>
                <Icon name={active ? "check_circle" : "chevron_right"} size={20} weight={500} />
              </button>
              {active && (
                <div className="mt-1.5 mb-1 px-1">
                  <input
                    data-testid={`input-injury-detail-${opt.flag}`}
                    value={selected?.detail ?? ""}
                    onChange={(e) => setDetail(opt.flag, e.target.value)}
                    placeholder="A short note for Coach Kiley, optional"
                    className="w-full mnf-body outline-none bg-transparent"
                    style={{
                      fontSize: 13, color: "var(--mnf-fd)",
                      borderBottom: "1px solid rgba(30,74,53,0.12)",
                      padding: "8px 4px",
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
        <div
          className="flex items-start gap-3 p-3 mt-2"
          style={{ background: "var(--mnf-fiberBg)", border: "1px solid var(--mnf-fiberBorder)", borderRadius: 16 }}
        >
          <span style={{ fontSize: 22 }} aria-hidden>🌿</span>
          <p className="mnf-body italic" style={{ fontSize: 12.5, color: "#2C6248", lineHeight: 1.5 }}>
            All clear? Skip ahead. Nothing here gets shared with your provider.
          </p>
        </div>
      </div>
      <Btn variant="primary" block size="lg" onClick={onNext} className="mt-6" data-testid="button-injuries-continue">
        Continue <Icon name="arrow_forward" size={16} />
      </Btn>
    </div>
  );
}

function MovementGoalsStep({
  value, onChange, onNext,
}: {
  value: FitnessGoal[];
  onChange: (v: FitnessGoal[]) => void;
  onNext: () => void;
}) {
  const toggle = (g: FitnessGoal) => {
    if (value.includes(g)) onChange(value.filter((x) => x !== g));
    else onChange([...value, g]);
  };
  return (
    <div className="flex-1 flex flex-col">
      <StepHeader
        eyebrow="Step 4 of 8"
        title="What does your strength season ask for?"
        sub="Pick one or two. Your first goal drives the set and rep scheme. Coach Kiley follows the NASM OPT model with a four-week compound block."
      />
      <div className="grid grid-cols-1 gap-2.5 flex-1">
        {MOVEMENT_GOAL_OPTIONS.map((g) => {
          const active = value.includes(g.value);
          return (
            <button
              key={g.value}
              onClick={() => toggle(g.value)}
              data-testid={`button-movement-goal-${g.value}`}
              className="w-full flex items-center gap-4 text-left transition-all hover-elevate active-elevate-2"
              style={{
                padding: "14px 16px",
                borderRadius: 18,
                background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)"}`,
                color: active ? "#F5F0E8" : "var(--mnf-fd)",
              }}
            >
              <span style={{ fontSize: 26 }} aria-hidden>{g.emoji}</span>
              <div className="flex-1">
                <p className="mnf-body" style={{ fontSize: 14, fontWeight: 700 }}>{g.label}</p>
                <p className="mnf-body" style={{ fontSize: 12, opacity: active ? 0.85 : 0.7, lineHeight: 1.4 }}>{g.sub}</p>
              </div>
              <Icon name={active ? "check_circle" : "chevron_right"} size={20} weight={500} />
            </button>
          );
        })}
      </div>
      <Btn variant="primary" block size="lg" onClick={onNext} disabled={value.length === 0} className="mt-6" data-testid="button-movement-goals-continue">
        Continue <Icon name="arrow_forward" size={16} />
      </Btn>
    </div>
  );
}

function ScheduleStep({
  days, minutes, onChange, onNext,
}: {
  days: WeekDay[];
  minutes: number;
  onChange: (days: WeekDay[], minutes: number) => void;
  onNext: () => void;
}) {
  const toggleDay = (d: WeekDay) => {
    if (days.includes(d)) onChange(days.filter((x) => x !== d), minutes);
    else onChange([...days, d].sort((a, b) => a - b) as WeekDay[], minutes);
  };
  const setMin = (m: number) => onChange(days, m);
  const minuteOptions = [20, 30, 45, 60];

  return (
    <div className="flex-1 flex flex-col">
      <StepHeader
        eyebrow="Step 5 of 8"
        title="When can you train?"
        sub="Days you can move plus how long. Coach builds the split. Three days gets full body, four splits upper and lower, five becomes push, pull, legs."
      />

      <GlassCard padding={16} className="mb-4">
        <p className="mnf-eyebrow mb-3" style={{ color: "var(--mnf-tg)" }}>Training days</p>
        <div className="flex justify-between gap-1.5">
          {WEEKDAY_OPTIONS.map((d) => {
            const active = days.includes(d.value);
            return (
              <button
                key={d.value}
                onClick={() => toggleDay(d.value)}
                data-testid={`button-day-${d.value}`}
                className="flex-1 flex flex-col items-center justify-center transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "10px 0",
                  borderRadius: 14,
                  background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.10)"}`,
                  color: active ? "#F5F0E8" : "var(--mnf-fd)",
                  minHeight: 56,
                }}
              >
                <span className="mnf-serif" style={{ fontSize: 18, fontWeight: 500 }}>{d.short}</span>
                <span className="mnf-body" style={{ fontSize: 9.5, letterSpacing: "0.06em", textTransform: "uppercase", opacity: active ? 0.85 : 0.6 }}>{d.label}</span>
              </button>
            );
          })}
        </div>
        <p className="mnf-body mt-3" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {days.length === 0
            ? "Pick at least one day."
            : days.length <= 3
              ? `${days.length} day${days.length === 1 ? "" : "s"} a week, full-body sessions.`
              : days.length === 4
                ? "4 days a week, upper and lower split."
                : `${days.length} days a week, push, pull, legs.`}
        </p>
      </GlassCard>

      <GlassCard padding={16} className="mb-4">
        <p className="mnf-eyebrow mb-3" style={{ color: "var(--mnf-tg)" }}>Minutes per day</p>
        <div className="grid grid-cols-4 gap-2">
          {minuteOptions.map((m) => {
            const active = minutes === m;
            return (
              <button
                key={m}
                onClick={() => setMin(m)}
                data-testid={`button-minutes-${m}`}
                className="flex flex-col items-center justify-center transition-all hover-elevate active-elevate-2"
                style={{
                  padding: "10px 0",
                  borderRadius: 14,
                  background: active ? "var(--mnf-t)" : "rgba(255,255,255,0.65)",
                  border: `1px solid ${active ? "var(--mnf-t)" : "rgba(30,74,53,0.10)"}`,
                  color: active ? "#fff" : "var(--mnf-fd)",
                }}
              >
                <span className="mnf-serif" style={{ fontSize: 22, fontWeight: 500 }}>{m}</span>
                <span className="mnf-body" style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", opacity: active ? 0.9 : 0.6 }}>min</span>
              </button>
            );
          })}
        </div>
        <p className="mnf-body mt-3" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {minutes >= 45
            ? "Two compounds, two accessories, core, and mobility."
            : minutes >= 30
              ? "One compound, one accessory, core, and mobility."
              : "One compound, one accessory, and core. Mobility on rest days."}
        </p>
      </GlassCard>

      <Btn variant="primary" block size="lg" onClick={onNext} disabled={days.length === 0} className="mt-auto" data-testid="button-schedule-continue">
        Continue <Icon name="arrow_forward" size={16} />
      </Btn>
    </div>
  );
}
